import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Row, Col } from 'react-bootstrap';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { searchCompanies, lookupStatusMessage } from '../../../../../services/api/companyLookupApi';
import { validateSwedishOrgNumber, formatOrgNumber } from '../../../../../utils/orgNumberUtils';

// Lucide React Icons
import {
  Building,
  Search,
  MapPin,
  Phone,
  Hash,
  Globe,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Företagsnamn är obligatoriskt'),
  orgnumber: Yup.string()
    .required('Org-nummer är obligatoriskt')
    .test('valid-orgnr', 'Ogiltigt organisationsnummer (XXXXXX-XXXX)', (value) =>
      !value || validateSwedishOrgNumber(value)
    ),
  vatnumber: Yup.string().nullable(),
  address: Yup.string().required('Adress är obligatoriskt'),
  city: Yup.string().required('Stad är obligatorisk'),
  postalcode: Yup.string().required('Postnummer är obligatoriskt'),
  country: Yup.string().required('Land är obligatoriskt'),
  phonenumber: Yup.string().nullable()
});

// Default empty form values. The TIC fields ride along invisibly when a
// company is picked from the register search — they are stored on the
// supplier row (F-skatt/VAT status for AP checks, sni/tic id for the agents).
const emptyFormValues = {
  supplier_id: '',
  orgnumber: '',
  vatnumber: '',
  name: '',
  address: '',
  city: '',
  postalcode: '',
  country: '',
  phonenumber: '',
  tic_company_id: null,
  legal_entity_type: '',
  f_tax_registered: null,
  vat_registered: null,
  sni_code: ''
};

const NewSupplierModal = ({ show, handleClose, handleSave, handleUpdate, initialSupplierData }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Configure Formik with enableReinitialize to reset when initialValues change
  const formik = useFormik({
    initialValues: emptyFormValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Distinguish between editing (has supplier_id) and creating (no supplier_id, even if OCR-prefilled)
        if (initialSupplierData?.supplier_id) {
          await handleUpdate(values);
        } else {
          await handleSave(values);
        }
        handleModalClose();
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const { values, errors, touched, setFieldValue, handleChange, handleSubmit, resetForm } = formik;

  // Set initial values when modal opens or initialSupplierData changes
  useEffect(() => {
    if (show) {
      if (initialSupplierData) {
        // Editing mode or OCR-prefill - populate form with supplier data
        resetForm({
          values: {
            supplier_id: initialSupplierData.supplier_id || '',
            orgnumber: initialSupplierData.orgnumber || '',
            vatnumber: initialSupplierData.vatnumber || '',
            name: initialSupplierData.name || '',
            address: initialSupplierData.postadress || initialSupplierData.postadress1 || initialSupplierData.address || '',
            city: initialSupplierData.city || '',
            postalcode: initialSupplierData.postnumber || initialSupplierData.postalcode || '',
            country: initialSupplierData.country || '',
            phonenumber: initialSupplierData.phone || initialSupplierData.phonenumber || '',
            tic_company_id: initialSupplierData.tic_company_id || null,
            legal_entity_type: initialSupplierData.legal_entity_type || '',
            f_tax_registered: initialSupplierData.f_tax_registered ?? null,
            vat_registered: initialSupplierData.vat_registered ?? null,
            sni_code: initialSupplierData.sni_code || ''
          }
        });
      } else {
        // Creation mode - reset to empty form
        resetForm({
          values: { ...emptyFormValues }
        });
      }

      // Reset the companies search results and selected company
      setCompanies([]);
      setSelectedCompany(null);
      setSearchQuery('');
      setSearchError(null);
    }
  }, [show, initialSupplierData, resetForm]);

  // Search via the backend TIC proxy — explicit action only (Sök button /
  // Enter), the TIC plan is ~200 calls/month platform-wide.
  const searchCompany = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 3) {
      setSearchError('Ange minst 3 tecken för att söka');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setCompanies([]);

    try {
      const result = await searchCompanies(inputValue.trim());
      if (!result.ok) {
        setSearchError(
          lookupStatusMessage(result.status) ||
          'Kunde inte hämta företagsinformation. Försök igen senare eller ange informationen manuellt.'
        );
        return;
      }
      if (!result.hits || result.hits.length === 0) {
        setSearchError('Inga företag hittades för denna sökning');
        return;
      }
      setCompanies(result.hits);
    } finally {
      setIsSearching(false);
    }
  };

  // `company` is a NormalizedHit — the backend already resolved legal name,
  // best address (mailing > visiting > registered), registry flags and the
  // derived VAT number, so this is a plain field copy.
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setCompanies([]);

    setFieldValue('orgnumber', formatOrgNumber(company.registrationNumber) || '');
    setFieldValue('vatnumber', company.vatNumber || '');
    setFieldValue('name', company.name || '');
    setFieldValue('address', company.address?.street || '');
    setFieldValue('city', company.address?.city || '');
    setFieldValue('postalcode', company.address?.postalCode || '');
    setFieldValue('country', company.country || 'SE');
    setFieldValue('phonenumber', company.phone || '');
    setFieldValue('tic_company_id', company.ticCompanyId || null);
    setFieldValue('legal_entity_type', company.legalEntityType || '');
    setFieldValue('f_tax_registered', company.fTaxRegistered ?? null);
    setFieldValue('vat_registered', company.vatRegistered ?? null);
    setFieldValue('sni_code', company.sniCode || '');
  };

  const handleModalClose = () => {
    // Reset form and state
    resetForm();
    setCompanies([]);
    setSelectedCompany(null);
    setSearchQuery('');
    setSearchError(null);
    setIsSearching(false);
    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Modal 
      size='lg' 
      show={show} 
      onHide={handleModalClose}
      centered
      backdrop="static"
    >
      <style>{`
        .modern-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modern-input {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 0.75rem;
          transition: all 0.2s ease;
          font-size: 0.875rem;
        }

        .modern-input:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }

        .modern-input.error {
          border-color: #dc2626;
        }

        .field-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          pointer-events: none;
        }

        .field-wrapper {
          position: relative;
        }

        .field-wrapper input {
          padding-left: 40px;
        }

        .validation-message {
          display: flex;
          align-items: center;
          margin-top: 0.5rem;
          font-size: 0.75rem;
        }

        .search-section {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .company-result {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .company-result:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
        }

        .modern-modal .section-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0;
          background: none;
          border: none;
          border-radius: 0;
          padding: 0;
        }

        .modern-modal .section-icon {
          color: #9ca3af;
          margin-right: 0.5rem;
          display: flex;
          align-items: center;
        }

        .modern-modal .section-title {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

      `}</style>

      <div className="modern-modal">
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            <Modal.Header style={{ borderBottom: '1px solid #e5e7eb', padding: '1.5rem' }}>
              <div className="d-flex align-items-center">
                <div 
                  className="me-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'var(--bs-primary)',
                    color: 'white'
                  }}
                >
                  <Building size={20} />
                </div>
                <div>
                  <Modal.Title style={{ margin: 0, fontWeight: '600' }}>
                    {initialSupplierData?.supplier_id ? 'Redigera leverantör' : 'Ny leverantör'}
                  </Modal.Title>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                    {initialSupplierData?.supplier_id ? 'Uppdatera leverantörsinformation' : 'Lägg till en ny leverantör i systemet'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline-secondary" 
                onClick={handleModalClose}
                style={{ 
                  border: 'none', 
                  background: '#f3f4f6',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} />
              </Button>
            </Modal.Header>

            <Modal.Body style={{ padding: '2rem' }}>
              {/* Company Search Section (TIC register lookup) */}
              {!initialSupplierData?.supplier_id && (
                <div className="search-section" data-testid="supplier-search-section">
                  <div className="d-flex align-items-center mb-3">
                    <Search size={14} style={{ color: '#6b7280', marginRight: '0.5rem' }} />
                    <span className="section-title">Sök i företagsregistret</span>
                  </div>

                  <Row>
                    <Col md={8}>
                      <div className="field-wrapper">
                        <Building className="field-icon" size={16} />
                        <input
                          type="text"
                          className="modern-input w-100"
                          placeholder="Företagsnamn eller organisationsnummer"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              searchCompany(searchQuery);
                            }
                          }}
                          disabled={isSearching}
                          data-testid="supplier-search-input"
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <Button
                        variant="outline-primary"
                        onClick={() => searchCompany(searchQuery)}
                        disabled={isSearching || searchQuery.trim().length < 3}
                        style={{
                          padding: '0.75rem 1rem',
                          width: '100%',
                          fontWeight: '500'
                        }}
                        data-testid="supplier-search-button"
                      >
                        {isSearching ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Söker...
                          </>
                        ) : (
                          <>
                            <Search size={16} className="me-2" />
                            Sök företag
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>

                  {searchError && (
                    <div className="validation-message text-danger" data-testid="supplier-search-error">
                      <AlertCircle size={12} className="me-1" />
                      {searchError}
                    </div>
                  )}

                  {companies.length > 0 && (
                    <div style={{ marginTop: '1rem' }} data-testid="supplier-search-results">
                      {companies.map((company, idx) => (
                        <div
                          key={company.ticCompanyId || idx}
                          className="company-result"
                          onClick={() => handleCompanySelect(company)}
                          data-testid={`supplier-company-result-${idx}`}
                        >
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>{company.name}</div>
                          <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                            Org.nr: {formatOrgNumber(company.registrationNumber)}
                            {company.address?.city ? ` · ${company.address.city}` : ''}
                            {company.isCeased ? ' · Upphört' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCompany && (
                    <div
                      className="d-flex align-items-center"
                      style={{ marginTop: '0.75rem', gap: '0.75rem', fontSize: '0.8125rem' }}
                      data-testid="supplier-selected-company"
                    >
                      <CheckCircle size={14} style={{ color: 'var(--bs-primary)' }} />
                      <span style={{ fontWeight: '600' }}>{selectedCompany.name}</span>
                      <span style={{
                        color: selectedCompany.fTaxRegistered ? '#16a34a' : '#6b7280',
                        fontWeight: 500
                      }}>
                        {selectedCompany.fTaxRegistered === true ? 'F-skatt ✓'
                          : selectedCompany.fTaxRegistered === false ? 'Ej F-skatt'
                          : ''}
                      </span>
                      <span style={{
                        color: selectedCompany.vatRegistered ? '#16a34a' : '#6b7280',
                        fontWeight: 500
                      }}>
                        {selectedCompany.vatRegistered === true ? 'Moms ✓'
                          : selectedCompany.vatRegistered === false ? 'Ej momsreg.'
                          : ''}
                      </span>
                    </div>
                  )}

                  <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                    Uppgifterna hämtas från det officiella företagsregistret. Du kan alltid fylla i eller justera manuellt nedan.
                  </p>
                </div>
              )}

              {/* Company Information Section */}
              <div className="mb-4">
                <div className="section-header">
                  <div className="section-icon">
                    <Building size={14} />
                  </div>
                  <span className="section-title">Företagsinformation</span>
                </div>

                <div className="mb-3">
                  <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                    Företagsnamn *
                  </label>
                  <div className="field-wrapper">
                    <Building className="field-icon" size={16} />
                    <input
                      type="text"
                      className={`modern-input w-100 ${touched.name && errors.name ? 'error' : ''}`}
                      placeholder="Företagsnamn"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                    />
                  </div>
                  {touched.name && errors.name && (
                    <div className="validation-message text-danger">
                      <AlertCircle size={12} className="me-1" />
                      {errors.name}
                    </div>
                  )}
                </div>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Org-nummer *
                      </label>
                      <div className="field-wrapper">
                        <Hash className="field-icon" size={16} />
                        <input
                          type="text"
                          className={`modern-input w-100 ${touched.orgnumber && errors.orgnumber ? 'error' : ''}`}
                          placeholder="XXXXXX-XXXX"
                          name="orgnumber"
                          value={values.orgnumber}
                          onChange={handleChange}
                        />
                      </div>
                      {touched.orgnumber && errors.orgnumber && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.orgnumber}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        VAT-nummer
                      </label>
                      <div className="field-wrapper">
                        <Globe className="field-icon" size={16} />
                        <input
                          type="text"
                          className={`modern-input w-100 ${touched.vatnumber && errors.vatnumber ? 'error' : ''}`}
                          placeholder="SEXXXXXXXXXX01"
                          name="vatnumber"
                          value={values.vatnumber}
                          onChange={handleChange}
                        />
                      </div>
                      {touched.vatnumber && errors.vatnumber && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.vatnumber}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Address Information Section */}
              <div className="mb-4">
                <div className="section-header">
                  <div className="section-icon">
                    <MapPin size={14} />
                  </div>
                  <span className="section-title">Adressinformation</span>
                </div>

                <div className="mb-3">
                  <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                    Adress *
                  </label>
                  <div className="field-wrapper">
                    <MapPin className="field-icon" size={16} />
                    <input
                      type="text"
                      className={`modern-input w-100 ${touched.address && errors.address ? 'error' : ''}`}
                      placeholder="Gatuadress"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                    />
                  </div>
                  {touched.address && errors.address && (
                    <div className="validation-message text-danger">
                      <AlertCircle size={12} className="me-1" />
                      {errors.address}
                    </div>
                  )}
                </div>

                <Row>
                  <Col md={4}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Postnummer *
                      </label>
                      <input
                        type="text"
                        className={`modern-input w-100 ${touched.postalcode && errors.postalcode ? 'error' : ''}`}
                        placeholder="123 45"
                        name="postalcode"
                        value={values.postalcode}
                        onChange={handleChange}
                      />
                      {touched.postalcode && errors.postalcode && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.postalcode}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Stad *
                      </label>
                      <input
                        type="text"
                        className={`modern-input w-100 ${touched.city && errors.city ? 'error' : ''}`}
                        placeholder="Stockholm"
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                      />
                      {touched.city && errors.city && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.city}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Land *
                      </label>
                      <input
                        type="text"
                        className={`modern-input w-100 ${touched.country && errors.country ? 'error' : ''}`}
                        placeholder="Sverige"
                        name="country"
                        value={values.country}
                        onChange={handleChange}
                      />
                      {touched.country && errors.country && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.country}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Contact Information Section */}
              <div>
                <div className="section-header">
                  <div className="section-icon">
                    <Phone size={14} />
                  </div>
                  <span className="section-title">Kontaktinformation</span>
                </div>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Telefonnummer
                      </label>
                      <div className="field-wrapper">
                        <Phone className="field-icon" size={16} />
                        <input
                          type="text"
                          className={`modern-input w-100 ${touched.phonenumber && errors.phonenumber ? 'error' : ''}`}
                          placeholder="+46 8 123 456 78"
                          name="phonenumber"
                          value={values.phonenumber}
                          onChange={handleChange}
                        />
                      </div>
                      {touched.phonenumber && errors.phonenumber && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.phonenumber}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </Modal.Body>

            <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '1.5rem' }}>
              <div className="d-flex justify-content-end gap-3 w-100">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  Avbryt
                </Button>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  style={{ 
                    background: 'var(--bs-primary)', 
                    borderColor: 'var(--bs-primary)',
                    padding: '0.75rem 1.5rem',
                    fontWeight: '600'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {initialSupplierData?.supplier_id ? 'Uppdaterar...' : 'Sparar...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="me-2" />
                      {initialSupplierData?.supplier_id ? 'Uppdatera leverantör' : 'Skapa leverantör'}
                    </>
                  )}
                </Button>
              </div>
            </Modal.Footer>
          </form>
        </FormikProvider>
      </div>
    </Modal>
  );
};

export default NewSupplierModal;